import React, { useEffect, useRef } from "react";
import useDocumentTitle from "../components/useDocumentTitle";
import "../assets/maximalist.css";

/* Letters split into spans so they can repel the cursor (kinetic hero). */
const kineticWord = (word) =>
  [...word].map((ch, i) => (
    <span className="kchar" key={i}>
      {ch}
    </span>
  ));

const Maximalist = () => {
  useDocumentTitle("Lakshay Parnami — Creative Technologist");
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const reduce = window.matchMedia("(prefers-reduced-motion:reduce)").matches;
    const touch = window.matchMedia("(hover:none)").matches;
    const $ = (sel) => root.querySelector(sel);
    const $$ = (sel) => [...root.querySelectorAll(sel)];

    // Track everything we set up so we can tear it all down on unmount.
    const rafs = [];
    const timers = [];
    const cleanups = [];
    const onWin = (type, fn, opts) => {
      window.addEventListener(type, fn, opts);
      cleanups.push(() => window.removeEventListener(type, fn, opts));
    };

    // Smooth in-page anchor scrolling (scoped: restored on unmount).
    const prevScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = "smooth";

    // Inject the display/mono fonts this design needs (Space Grotesk already global).
    const fontLink = document.createElement("link");
    fontLink.rel = "stylesheet";
    fontLink.href =
      "https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=JetBrains+Mono:wght@400;500;700&display=swap";
    document.head.appendChild(fontLink);

    const cssVar = (v) => getComputedStyle(root).getPropertyValue(v).trim();

    /* ░░ FIT BIG TYPE TO SCREEN ░░
       The hero name + "LET'S TALK" are sized so the widest line exactly
       fills its container — no horizontal overflow at any viewport width. */
    function fitText() {
      const REF = 200; // measure at a reference size, then scale

      // Hero: keep both lines the same size, sized to the wider one.
      const heroTitle = $("#heroTitle");
      const lines = $$(".hero__line");
      if (heroTitle && lines.length) {
        const avail = heroTitle.clientWidth;
        if (avail > 0) {
          let scale = Infinity;
          lines.forEach((l) => {
            l.style.fontSize = REF + "px";
            if (l.scrollWidth) scale = Math.min(scale, avail / l.scrollWidth);
          });
          if (isFinite(scale) && scale > 0) {
            const size = REF * scale;
            lines.forEach((l) => { l.style.fontSize = size + "px"; });
          }
        } else {
          // No measurable width yet — fall back to the CSS clamp.
          lines.forEach((l) => { l.style.fontSize = ""; });
        }
      }

      // Contact: fit "LET'S TALK" within its centered column.
      const big = $(".contact__big");
      if (big && big.parentElement) {
        const avail = big.parentElement.clientWidth;
        if (avail > 0) {
          big.style.fontSize = REF + "px";
          if (big.scrollWidth) big.style.fontSize = (REF * avail) / big.scrollWidth + "px";
        } else {
          big.style.fontSize = "";
        }
      }
    }
    fitText();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitText);
    timers.push(setTimeout(fitText, 600)); // fallback once webfont swaps in
    onWin("resize", fitText);

    /* ░░ BOOT SEQUENCE ░░ */
    const boot = $("#boot");
    const fill = $("#bootFill");
    const pct = $("#bootPct");
    const log = $("#bootLog");
    const LOGS = [
      "initializing runtime…", "loading shaders…", "spawning particles…",
      "warming up physics…", "calibrating cursor…", "hello world ✦",
    ];
    let p = 0, li = 0;
    function boott() {
      p += Math.random() * 9 + 3;
      if (p > 100) p = 100;
      if (fill) fill.style.width = p + "%";
      if (pct) pct.textContent = String(Math.floor(p)).padStart(3, "0");
      const idx = Math.min(LOGS.length - 1, Math.floor((p / 100) * LOGS.length));
      if (idx !== li && log) { li = idx; log.textContent = LOGS[idx]; }
      if (p < 100) timers.push(setTimeout(boott, 90 + Math.random() * 110));
      else timers.push(setTimeout(() => { boot && boot.classList.add("done"); kickReveals(); }, 380));
    }
    if (reduce) { boot && boot.classList.add("done"); }
    else timers.push(setTimeout(boott, 280));

    /* ░░ SCRAMBLE TEXT ░░ */
    const GLYPHS = "█▓▒░<>/\\=+*#@$%&ABCDEFGHJKLMNPQRSTUVWXYZ0123456789";
    function scramble(el, dur = 900) {
      const final = el.getAttribute("data-text") || el.textContent;
      const len = final.length;
      let frame = 0;
      const total = Math.round(dur / 16);
      const reveal = new Array(len).fill(0).map(() => Math.random() * 0.7);
      function tick() {
        let out = "";
        for (let i = 0; i < len; i++) {
          const prog = frame / total;
          if (prog > reveal[i]) out += final[i];
          else if (final[i] === " ") out += " ";
          else out += GLYPHS[(Math.random() * GLYPHS.length) | 0];
        }
        el.textContent = out;
        frame++;
        if (frame <= total) rafs.push(requestAnimationFrame(tick));
        else el.textContent = final;
      }
      tick();
    }

    /* ░░ SPLIT WORDS for staggered reveal ░░ */
    $$(".reveal-words").forEach((el) => {
      if (el.dataset.split) return;
      el.dataset.split = "1";
      const tmp = document.createElement("div");
      tmp.innerHTML = el.innerHTML;
      function walk(node, out) {
        node.childNodes.forEach((n) => {
          if (n.nodeType === 3) {
            n.textContent.split(/(\s+)/).forEach((w) => {
              if (w.trim() === "") { out.push(document.createTextNode(w)); return; }
              const s = document.createElement("span");
              s.className = "word"; s.textContent = w;
              out.push(s);
            });
          } else {
            const clone = n.cloneNode(false);
            if (clone.classList && n.textContent.trim()) {
              const inner = [];
              walk(n, inner);
              clone.innerHTML = "";
              inner.forEach((c) => clone.appendChild(c));
              if (!clone.classList.contains("word")) clone.classList.add("word");
            }
            out.push(clone);
          }
        });
      }
      const result = [];
      walk(tmp, result);
      el.innerHTML = "";
      result.forEach((n) => el.appendChild(n));
      el.querySelectorAll(".word").forEach((w, i) => { w.style.transitionDelay = i * 0.04 + "s"; });
    });

    /* ░░ REVEAL ON SCROLL ░░ */
    const io = new IntersectionObserver(
      (ents) => {
        ents.forEach((ent) => {
          if (ent.isIntersecting) {
            ent.target.classList.add("in");
            if (ent.target.classList.contains("about__col")) startCounters();
            io.unobserve(ent.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    $$(".reveal, .reveal-words").forEach((el) => io.observe(el));
    cleanups.push(() => io.disconnect());

    function kickReveals() {
      $$(".hero .reveal").forEach((el, i) => {
        timers.push(setTimeout(() => el.classList.add("in"), 120 + i * 130));
      });
      $$(".scramble").forEach((el, i) => {
        timers.push(setTimeout(() => scramble(el, 1000), 200 + i * 120));
      });
    }

    /* ░░ COUNTERS ░░ */
    let counted = false;
    function startCounters() {
      if (counted) return;
      counted = true;
      $$(".num[data-count]").forEach((el) => {
        const target = +el.getAttribute("data-count");
        const t0 = performance.now(), dur = 1400;
        function tick(now) {
          const k = Math.min(1, (now - t0) / dur);
          const e = 1 - Math.pow(1 - k, 3);
          el.textContent = Math.round(target * e);
          if (k < 1) rafs.push(requestAnimationFrame(tick));
        }
        rafs.push(requestAnimationFrame(tick));
      });
    }

    /* ░░ SCRAMBLE ON HOVER ░░ */
    $$(".scramble-hover").forEach((el) => {
      el.setAttribute("data-text", el.textContent);
      const host = el.closest("a, button");
      if (host) {
        const fn = () => scramble(el, 600);
        host.addEventListener("mouseenter", fn);
        cleanups.push(() => host.removeEventListener("mouseenter", fn));
      }
    });

    /* ░░ PROJECT TILT + spotlight ░░ */
    $$(".proj.tilt").forEach((card) => {
      const move = (e) => {
        const r = card.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width;
        const py = (e.clientY - r.top) / r.height;
        card.style.setProperty("--mx", px * 100 + "%");
        card.style.setProperty("--my", py * 100 + "%");
        const rx = (py - 0.5) * -10, ry = (px - 0.5) * 12;
        card.style.transform = `perspective(800px) rotateX(${rx}deg) rotateY(${ry}deg)`;
      };
      const leave = () => { card.style.transform = "perspective(800px) rotateX(0) rotateY(0)"; };
      card.addEventListener("mousemove", move);
      card.addEventListener("mouseleave", leave);
      cleanups.push(() => { card.removeEventListener("mousemove", move); card.removeEventListener("mouseleave", leave); });
    });

    /* ░░ THEME SWITCHER ░░ */
    const dock = $("#themeDock");
    const saved = localStorage.getItem("lp-ott-theme");
    if (saved) root.setAttribute("data-theme", saved);
    function syncChips() {
      dock && dock.querySelectorAll(".theme-chip").forEach((b) => {
        b.classList.toggle("active", b.dataset.themeSet === root.getAttribute("data-theme"));
      });
    }
    if (dock) {
      const onDock = (e) => {
        const b = e.target.closest(".theme-chip");
        if (!b) return;
        root.setAttribute("data-theme", b.dataset.themeSet);
        localStorage.setItem("lp-ott-theme", b.dataset.themeSet);
        syncChips();
      };
      dock.addEventListener("click", onDock);
      cleanups.push(() => dock.removeEventListener("click", onDock));
      syncChips();
    }

    /* ░░ NAV hide on scroll down ░░ */
    const nav = $("#nav");
    let lastY = 0;
    onWin("scroll", () => {
      const y = window.scrollY;
      if (nav) {
        if (y > lastY && y > 200) nav.classList.add("hide");
        else nav.classList.remove("hide");
      }
      lastY = y;
    }, { passive: true });

    /* ░░ CUSTOM CURSOR + MAGNETIC ░░ */
    if (!touch) {
      const cursor = $("#cursor");
      const ring = $("#cursorRing");
      const dot = $("#cursorDot");
      const label = $("#cursorLabel");
      let mx = window.innerWidth / 2, my = window.innerHeight / 2;
      let rx = mx, ry = my, lx = mx, ly = my;
      const LABELS = {
        home: "TOP", go: "GO", explore: "EXPLORE", open: "OPEN",
        view: "VIEW", read: "READ", email: "MAIL", up: "UP", drag: "DRAG",
      };
      onWin("mousemove", (e) => { mx = e.clientX; my = e.clientY; }, { passive: true });
      const down = () => cursor && cursor.classList.add("is-down");
      const up = () => cursor && cursor.classList.remove("is-down");
      onWin("mousedown", down);
      onWin("mouseup", up);

      let cursorRAF;
      function craf() {
        rx += (mx - rx) * 0.18; ry += (my - ry) * 0.18;
        lx += (mx - lx) * 0.3; ly += (my - ly) * 0.3;
        if (dot) { dot.style.left = mx + "px"; dot.style.top = my + "px"; }
        if (ring) { ring.style.left = rx + "px"; ring.style.top = ry + "px"; }
        if (label) { label.style.left = lx + "px"; label.style.top = ly + 34 + "px"; }
        cursorRAF = requestAnimationFrame(craf);
      }
      craf();
      cleanups.push(() => cancelAnimationFrame(cursorRAF));

      $$("a, button, .magnetic, [data-cursor], input, textarea").forEach((el) => {
        const enter = () => {
          cursor && cursor.classList.add("is-hover");
          const key = el.getAttribute("data-cursor");
          if (label) label.textContent = key && LABELS[key] ? LABELS[key] : "";
        };
        const leave = () => {
          cursor && cursor.classList.remove("is-hover");
          if (label) label.textContent = "";
        };
        el.addEventListener("mouseenter", enter);
        el.addEventListener("mouseleave", leave);
        cleanups.push(() => { el.removeEventListener("mouseenter", enter); el.removeEventListener("mouseleave", leave); });
      });

      $$(".magnetic").forEach((el) => {
        const strength = el.classList.contains("contact__big") ? 0.32 : 0.42;
        const move = (e) => {
          const r = el.getBoundingClientRect();
          const x = e.clientX - (r.left + r.width / 2);
          const y = e.clientY - (r.top + r.height / 2);
          el.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
        };
        const leave = () => {
          el.style.transform = "translate(0,0)";
          el.style.transition = "transform .5s cubic-bezier(.16,1,.3,1)";
          timers.push(setTimeout(() => { el.style.transition = ""; }, 500));
        };
        el.addEventListener("mousemove", move);
        el.addEventListener("mouseleave", leave);
        cleanups.push(() => { el.removeEventListener("mousemove", move); el.removeEventListener("mouseleave", leave); });
      });
    }

    /* ░░ HERO — particle field + kinetic letters ░░ */
    (function hero() {
      const canvas = $("#heroCanvas");
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      let W, H, dpr;
      const mouse = { x: -9999, y: -9999 };
      let particles = [];
      let alive = true;

      function resize() {
        dpr = Math.min(window.devicePixelRatio || 1, 2);
        W = canvas.clientWidth; H = canvas.clientHeight;
        canvas.width = W * dpr; canvas.height = H * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        const count = Math.min(120, Math.floor((W * H) / 14000));
        particles = [];
        for (let i = 0; i < count; i++) {
          particles.push({
            x: Math.random() * W, y: Math.random() * H,
            vx: (Math.random() - 0.5) * 0.35, vy: (Math.random() - 0.5) * 0.35,
            r: Math.random() * 1.8 + 0.6,
          });
        }
      }
      const heroEl = $("#hero");
      const hMove = (e) => {
        const r = canvas.getBoundingClientRect();
        mouse.x = e.clientX - r.left; mouse.y = e.clientY - r.top;
      };
      const hLeave = () => { mouse.x = -9999; mouse.y = -9999; };
      if (heroEl) {
        heroEl.addEventListener("mousemove", hMove);
        heroEl.addEventListener("mouseleave", hLeave);
        cleanups.push(() => { heroEl.removeEventListener("mousemove", hMove); heroEl.removeEventListener("mouseleave", hLeave); });
      }

      let heroRAF;
      function draw() {
        if (!alive) return;
        ctx.clearRect(0, 0, W, H);
        const c1 = cssVar("--c1") || "#00e6ff", c2 = cssVar("--c2") || "#ff2bd6";
        for (let i = 0; i < particles.length; i++) {
          const pt = particles[i];
          pt.x += pt.vx; pt.y += pt.vy;
          const dxm = pt.x - mouse.x, dym = pt.y - mouse.y;
          const dm = Math.hypot(dxm, dym);
          if (dm < 150) { const f = ((150 - dm) / 150) * 0.8; pt.x += (dxm / dm) * f; pt.y += (dym / dm) * f; }
          if (pt.x < 0 || pt.x > W) pt.vx *= -1;
          if (pt.y < 0 || pt.y > H) pt.vy *= -1;
          for (let j = i + 1; j < particles.length; j++) {
            const q = particles[j];
            const dx = pt.x - q.x, dy = pt.y - q.y;
            const d = Math.hypot(dx, dy);
            if (d < 120) {
              ctx.globalAlpha = (1 - d / 120) * 0.18;
              ctx.strokeStyle = c1; ctx.lineWidth = 1;
              ctx.beginPath(); ctx.moveTo(pt.x, pt.y); ctx.lineTo(q.x, q.y); ctx.stroke();
            }
          }
        }
        for (const pt of particles) {
          const dm = Math.hypot(pt.x - mouse.x, pt.y - mouse.y);
          ctx.globalAlpha = 0.7;
          ctx.fillStyle = dm < 150 ? c2 : c1;
          ctx.beginPath(); ctx.arc(pt.x, pt.y, pt.r, 0, 7); ctx.fill();
        }
        ctx.globalAlpha = 1;
        heroRAF = requestAnimationFrame(draw);
      }
      onWin("resize", resize);
      resize(); draw();
      cleanups.push(() => { alive = false; cancelAnimationFrame(heroRAF); });

      const chars = $$(".hero__title .kchar");
      let pmx = -9999, pmy = -9999;
      onWin("mousemove", (e) => { pmx = e.clientX; pmy = e.clientY; }, { passive: true });
      // Continuous loop: each letter has a perpetual idle wave + a much
      // stronger, springier repulsion when the cursor gets near.
      const t0 = performance.now();
      let kineticRAF;
      function kinetic() {
        const t = (performance.now() - t0) / 1000;
        const radius = 240;
        chars.forEach((ch, i) => {
          // Idle: gentle per-letter sine bob + sway so the name is always alive.
          const wave = Math.sin(t * 2.1 + i * 0.55);
          const sway = Math.cos(t * 1.4 + i * 0.4);
          let tx = sway * 5;
          let ty = wave * 7;
          let rot = wave * 3;
          let scale = 1 + wave * 0.04;
          const r = ch.getBoundingClientRect();
          const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
          const dx = cx - pmx, dy = cy - pmy;
          const dist = Math.hypot(dx, dy) || 1;
          if (dist < radius) {
            const f = (radius - dist) / radius;
            const ease = f * f; // sharper falloff = punchier shove
            tx += (dx / dist) * ease * 70;
            ty += (dy / dist) * ease * 70;
            rot += ease * (dx > 0 ? 18 : -18);
            scale += ease * 0.35;
          }
          ch.style.transform = `translate(${tx}px, ${ty}px) rotate(${rot}deg) scale(${scale})`;
        });
        kineticRAF = requestAnimationFrame(kinetic);
      }
      if (!reduce) { kinetic(); cleanups.push(() => cancelAnimationFrame(kineticRAF)); }
    })();

    /* ░░ STACK — draggable physics playground ░░ */
    (function physics() {
      const canvas = $("#physicsCanvas");
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      const wrap = $("#playground");
      let W, H, dpr;
      const SKILLS = [
        "Kotlin", "Flutter", "Dart", "Jetpack Compose", "Android",
        "Coroutines", "Kotlin Flow", "Clean Arch", "MVVM / MVI", "Hilt",
        "Room", "WorkManager", "Firebase", "TypeScript", "AI-Driven Dev",
      ];
      let chips = [];
      let started = false;
      let ran = false;
      let alive = true;

      function measure(text) {
        ctx.font = "700 15px 'JetBrains Mono', monospace";
        return ctx.measureText(text).width;
      }
      function build() {
        chips = SKILLS.map((s, i) => {
          const w = measure(s) + 40;
          const h = 40;
          return {
            text: s, w, h,
            x: 40 + Math.random() * Math.max(40, W - 80 - w),
            y: 20 + Math.random() * 60 + i * 4,
            vx: (Math.random() - 0.5) * 2, vy: 0,
            accent: i % 3 === 0,
          };
        });
      }
      function resize() {
        dpr = Math.min(window.devicePixelRatio || 1, 2);
        W = wrap.clientWidth; H = wrap.clientHeight;
        canvas.width = W * dpr; canvas.height = H * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        if (!chips.length) build();
      }
      const G = 0.45, FR = 0.99, BOUNCE = 0.55;
      let drag = null, dragOff = { x: 0, y: 0 }, pointer = { x: 0, y: 0 }, lastP = { x: 0, y: 0 };
      let physRAF;
      function step() {
        if (!alive) return;
        ctx.clearRect(0, 0, W, H);
        const c1 = cssVar("--c1"), c2 = cssVar("--c2"), bg = cssVar("--bg"), line = cssVar("--line"), fg = cssVar("--fg");
        for (const c of chips) {
          if (c === drag) {
            c.x = pointer.x - dragOff.x; c.y = pointer.y - dragOff.y;
            c.vx = pointer.x - lastP.x; c.vy = pointer.y - lastP.y;
          } else {
            c.vy += G; c.vx *= FR; c.vy *= FR; c.x += c.vx; c.y += c.vy;
            if (c.x < 0) { c.x = 0; c.vx *= -BOUNCE; }
            if (c.x + c.w > W) { c.x = W - c.w; c.vx *= -BOUNCE; }
            if (c.y + c.h > H) { c.y = H - c.h; c.vy *= -BOUNCE; c.vx *= 0.96; }
            if (c.y < 0) { c.y = 0; c.vy *= -BOUNCE; }
          }
        }
        for (let i = 0; i < chips.length; i++) {
          for (let j = i + 1; j < chips.length; j++) {
            const a = chips[i], b = chips[j];
            if (a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y) {
              const ox = Math.min(a.x + a.w - b.x, b.x + b.w - a.x);
              const oy = Math.min(a.y + a.h - b.y, b.y + b.h - a.y);
              if (ox < oy) {
                const s = (a.x < b.x ? -1 : 1) * ox / 2;
                if (a !== drag) a.x += s; if (b !== drag) b.x -= s;
                if (a !== drag) a.vx = b.vx * 0.5; if (b !== drag) b.vx = a.vx * 0.5;
              } else {
                const s = (a.y < b.y ? -1 : 1) * oy / 2;
                if (a !== drag) a.y += s; if (b !== drag) b.y -= s;
                const t = a.vy; if (a !== drag) a.vy = b.vy * 0.5; if (b !== drag) b.vy = t * 0.5;
              }
            }
          }
        }
        ctx.font = "700 15px 'JetBrains Mono', monospace";
        ctx.textBaseline = "middle";
        for (const c of chips) {
          const r = c.h / 2;
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(c.x, c.y, c.w, c.h, r);
          else ctx.rect(c.x, c.y, c.w, c.h);
          ctx.fillStyle = c.accent ? c1 : bg; ctx.fill();
          ctx.lineWidth = 1.5;
          ctx.strokeStyle = c.accent ? c1 : c === drag ? c2 : line; ctx.stroke();
          ctx.fillStyle = c.accent ? bg : fg;
          ctx.fillText(c.text, c.x + 20, c.y + c.h / 2 + 1);
        }
        lastP.x = pointer.x; lastP.y = pointer.y;
        physRAF = requestAnimationFrame(step);
      }
      function localPt(e) {
        const r = canvas.getBoundingClientRect();
        const cx = e.touches ? e.touches[0].clientX : e.clientX;
        const cy = e.touches ? e.touches[0].clientY : e.clientY;
        return { x: cx - r.left, y: cy - r.top };
      }
      function hit(pt) {
        for (let i = chips.length - 1; i >= 0; i--) {
          const c = chips[i];
          if (pt.x >= c.x && pt.x <= c.x + c.w && pt.y >= c.y && pt.y <= c.y + c.h) return c;
        }
        return null;
      }
      const pdown = (e) => {
        const pt = localPt(e); pointer = pt; lastP = { ...pt };
        const c = hit(pt);
        if (c) { drag = c; dragOff = { x: pt.x - c.x, y: pt.y - c.y }; canvas.setPointerCapture(e.pointerId); }
      };
      const pmove = (e) => { pointer = localPt(e); };
      const pend = () => { drag = null; };
      canvas.addEventListener("pointerdown", pdown);
      canvas.addEventListener("pointermove", pmove);
      canvas.addEventListener("pointerup", pend);
      canvas.addEventListener("pointercancel", pend);
      cleanups.push(() => {
        canvas.removeEventListener("pointerdown", pdown);
        canvas.removeEventListener("pointermove", pmove);
        canvas.removeEventListener("pointerup", pend);
        canvas.removeEventListener("pointercancel", pend);
      });
      onWin("resize", resize);
      resize();
      const pio = new IntersectionObserver(
        (ents) => {
          ents.forEach((ent) => {
            if (ent.isIntersecting && !started) {
              started = true; build();
              if (!ran) { ran = true; step(); }
            }
          });
        },
        { threshold: 0.2 }
      );
      pio.observe(wrap);
      timers.push(setTimeout(() => { if (!ran) { ran = true; step(); } }, 1200));
      cleanups.push(() => { alive = false; cancelAnimationFrame(physRAF); pio.disconnect(); });
    })();

    /* ░░ TEARDOWN ░░ */
    return () => {
      rafs.forEach((id) => cancelAnimationFrame(id));
      timers.forEach((id) => clearTimeout(id));
      cleanups.forEach((fn) => fn());
      document.documentElement.style.scrollBehavior = prevScrollBehavior;
      if (fontLink.parentNode) fontLink.parentNode.removeChild(fontLink);
    };
  }, []);

  return (
    <div className="ott" data-theme="neon" ref={rootRef}>
      {/* ░░ GRAIN / SCANLINE / GRID OVERLAYS ░░ */}
      <div className="fx-grid" aria-hidden="true" />
      <div className="fx-scan" aria-hidden="true" />
      <div className="fx-noise" aria-hidden="true" />
      <div className="fx-vignette" aria-hidden="true" />

      {/* ░░ CUSTOM CURSOR ░░ */}
      <div className="cursor" id="cursor" aria-hidden="true">
        <div className="cursor__ring" id="cursorRing" />
        <div className="cursor__dot" id="cursorDot" />
        <div className="cursor__label" id="cursorLabel" />
      </div>

      {/* ░░ BOOT / LOADER ░░ */}
      <div className="boot" id="boot">
        <div className="boot__inner">
          <div className="boot__row">
            <span className="boot__tag">SYS</span>
            <span id="bootLog">initializing runtime…</span>
          </div>
          <div className="boot__bar"><div className="boot__fill" id="bootFill" /></div>
          <div className="boot__meta">
            <span id="bootPct">000</span><span>%</span>
            <span className="boot__name" id="bootName">LAKSHAY&nbsp;PARNAMI</span>
          </div>
        </div>
      </div>

      {/* ░░ THEME SWITCHER ░░ */}
      <div className="theme-dock" id="themeDock" aria-label="Theme switcher">
        <button className="theme-chip" data-theme-set="neon" style={{ "--c1": "#00e6ff", "--c2": "#ff2bd6" }} title="Neon" />
        <button className="theme-chip" data-theme-set="acid" style={{ "--c1": "#ccff00", "--c2": "#ff5e00" }} title="Acid" />
        <button className="theme-chip" data-theme-set="vapor" style={{ "--c1": "#b066ff", "--c2": "#ff5fa2" }} title="Vapor" />
        <button className="theme-chip" data-theme-set="solar" style={{ "--c1": "#ff3b1d", "--c2": "#1d4eff" }} title="Solar" />
      </div>

      {/* ░░ NAV ░░ */}
      <header className="nav" id="nav">
        <a className="nav__brand magnetic" href="#top" data-cursor="home">LP<span className="nav__blink">_</span></a>
        <nav className="nav__links">
          <a href="#about" className="magnetic" data-cursor="go">About</a>
          <a href="#work" className="magnetic" data-cursor="go">Work</a>
          <a href="#stack" className="magnetic" data-cursor="go">Stack</a>
          <a href="#education" className="magnetic" data-cursor="go">Education</a>
          <a href="#contact" className="magnetic nav__cta" data-cursor="go">Contact</a>
        </nav>
      </header>

      <main id="top">
        {/* ░░ HERO ░░ */}
        <section className="hero" id="hero" data-screen-label="Hero">
          <canvas className="hero__canvas" id="heroCanvas" />
          <div className="hero__content">
            <div className="hero__eyebrow reveal">
              <span className="dot-pulse" />
              <span className="scramble" data-text="SDE-4 ANDROID @ PORTER">SDE-4 ANDROID @ PORTER</span>
            </div>
            <h1 className="hero__title" id="heroTitle" aria-label="Lakshay Parnami">
              <span className="hero__line" data-line>{kineticWord("LAKSHAY")}</span>
              <span className="hero__line hero__line--2" data-line>{kineticWord("PARNAMI")}</span>
            </h1>
            <div className="hero__sub reveal">
              <p>
                I develop mobile applications of all formats — <span className="hl">Kotlin</span>,{" "}
                <span className="hl">Flutter</span> &amp; <span className="hl">Jetpack&nbsp;Compose</span>.
                I build <em>scalable</em>, developer-friendly architectures that ship.
              </p>
            </div>
            <div className="hero__cta reveal">
              <a
                href={`${process.env.PUBLIC_URL}/resume-lakshay-parnami.pdf`}
                className="btn btn--primary magnetic"
                data-cursor="open"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>Download Resume</span><span className="btn__arrow">↓</span>
              </a>
              <a href="#contact" className="btn btn--ghost magnetic" data-cursor="go"><span>Say hello</span></a>
            </div>
          </div>
          <div className="hero__scroll reveal" aria-hidden="true">
            <span>SCROLL</span><span className="hero__scrollbar"><i /></span>
          </div>
        </section>

        {/* ░░ MARQUEE ░░ */}
        <div className="marquee" id="marquee1" aria-hidden="true">
          <div className="marquee__track">
            <span>KOTLIN ✦ ANDROID ✦ FLUTTER ✦ JETPACK COMPOSE ✦ CLEAN ARCHITECTURE ✦ COROUTINES ✦ FIREBASE ✦ AI-DRIVEN DEV ✦&nbsp;</span>
            <span>KOTLIN ✦ ANDROID ✦ FLUTTER ✦ JETPACK COMPOSE ✦ CLEAN ARCHITECTURE ✦ COROUTINES ✦ FIREBASE ✦ AI-DRIVEN DEV ✦&nbsp;</span>
          </div>
        </div>

        {/* ░░ ABOUT ░░ */}
        <section className="about" id="about" data-screen-label="About">
          <div className="section-head reveal">
            <span className="section-no">01</span>
            <span className="section-kicker">{"// about"}</span>
          </div>
          <div className="about__grid">
            <h2 className="about__statement reveal-words">
              I build production mobile apps that feel <span className="hl">alive</span> — fast, tactile, and architected to <span className="hl">scale</span>.
            </h2>
            <div className="about__col reveal">
              <p>
                Right now I'm <strong>SDE-4 Android at Porter</strong>, leading a team of engineers and architecting
                systems that handle <strong>~1.5K requests/sec</strong>. Before that I shipped Android &amp; Flutter
                products at <strong>YourStory</strong>, <strong>GoDutch</strong> &amp; <strong>CrimzonCode</strong>.
              </p>
              <p>
                My happy place is the seam between <em>design and engineering</em> — Kotlin, Jetpack Compose,
                Clean Architecture, and the details nobody asked for but everybody feels.
              </p>
              <ul className="about__facts">
                <li><span className="num" data-count="7">0</span><span>+ years building mobile</span></li>
                <li><span className="num" data-count="6">0</span><span> companies shipped for</span></li>
                <li><span className="num" data-count="1500">0</span><span>+ requests/sec handled</span></li>
              </ul>
            </div>
          </div>
        </section>

        {/* ░░ WORK / EXPERIENCE ░░ */}
        <section className="work" id="work" data-screen-label="Work">
          <div className="section-head reveal">
            <span className="section-no">02</span>
            <span className="section-kicker">{"// experience & projects"}</span>
          </div>

          <div className="timeline" id="timeline">
            <article className="job reveal" data-cursor="open">
              <div className="job__role">
                <span className="job__company">Porter</span>
                <span className="job__title">SDE-4 Android · prev. SDE-3 · Sept 2022 – Present</span>
              </div>
              <p className="job__desc">
                Leading a team of 5 engineers on logistics platforms. Built a <strong>vehicle cataloging platform</strong> powering
                vehicle rollout &amp; partner onboarding, and architected <strong>real-time driver tracking</strong> handling ~1.5K requests/sec.
              </p>
              <div className="job__tags"><span>Kotlin</span><span>Architecture</span><span>Scale</span><span>Leadership</span></div>
            </article>
            <article className="job reveal" data-cursor="open">
              <div className="job__role">
                <span className="job__company">GoDutch</span>
                <span className="job__title">Sr. Software Developer · Jan 2022 – Sept 2022</span>
              </div>
              <p className="job__desc">
                Contributed to the <strong>GoDutch Android app</strong> and led a brand-new <strong>Flutter</strong> project from scratch.
              </p>
              <div className="job__tags"><span>Flutter</span><span>Android</span><span>Fintech</span></div>
            </article>
            <article className="job reveal" data-cursor="open">
              <div className="job__role">
                <span className="job__company">YourStory</span>
                <span className="job__title">Sr. Software Developer · Feb 2020 – Jan 2022</span>
              </div>
              <p className="job__desc">
                Designed &amp; developed the <strong>YourStory native Android app</strong>, plus a <strong>Flutter app</strong> from scratch.
              </p>
              <div className="job__tags"><span>Android</span><span>Flutter</span><span>Media</span></div>
            </article>
            <article className="job reveal" data-cursor="open">
              <div className="job__role">
                <span className="job__company">CrimzonCode</span>
                <span className="job__title">Software Developer — Android · Feb 2018 – Feb 2020</span>
              </div>
              <p className="job__desc">
                Built &amp; managed the <strong>TopQuiz</strong>, <strong>BattleKing</strong> and <strong>MobileBaazi</strong> real-money quiz gaming apps.
              </p>
              <div className="job__tags"><span>Android</span><span>Gaming</span><span>Real-money</span></div>
            </article>
            <article className="job reveal" data-cursor="open">
              <div className="job__role">
                <span className="job__company">Paperwork Consulting</span>
                <span className="job__title">Head of Product Development · Sep 2017 – Dec 2017</span>
              </div>
              <p className="job__desc">
                Built <strong>BingeTales</strong> — a first-of-its-kind reading app organising content across Horror, Mythology, Mystery, Classics &amp; more.
              </p>
              <div className="job__tags"><span>Android</span><span>Product</span><span>Content</span></div>
            </article>
            <article className="job reveal" data-cursor="open">
              <div className="job__role">
                <span className="job__company">ClinMD</span>
                <span className="job__title">Android Developer Intern · Jul 2017 – Sept 2017</span>
              </div>
              <p className="job__desc">
                Built a <strong>prescription-writing app</strong> interfaced with a smart pen over Bluetooth.
              </p>
              <div className="job__tags"><span>Android</span><span>Bluetooth</span><span>Health</span></div>
            </article>
          </div>

          <div className="projects" id="projects">
            <h3 className="projects__head reveal">Selected builds</h3>
            <div className="proj-grid">
              <a className="proj tilt magnetic" data-cursor="view" href="#work" data-proj="1">
                <div className="proj__id">P/01</div>
                <h4>Vehicle Cataloging Platform</h4>
                <p>Powers vehicle rollout &amp; partner onboarding across Porter's fleet operations.</p>
                <div className="proj__tags"><span>Kotlin</span><span>Architecture</span></div>
              </a>
              <a className="proj tilt magnetic" data-cursor="view" href="#work" data-proj="2">
                <div className="proj__id">P/02</div>
                <h4>Real-time Driver Tracking</h4>
                <p>Live location services architected to handle ~1.5K requests per second.</p>
                <div className="proj__tags"><span>Backend</span><span>Scale</span></div>
              </a>
              <a className="proj tilt magnetic" data-cursor="view" href="#work" data-proj="3">
                <div className="proj__id">P/03</div>
                <h4>Quiz Gaming Apps</h4>
                <p>TopQuiz, BattleKing &amp; MobileBaazi — real-money quiz games shipped at scale.</p>
                <div className="proj__tags"><span>Android</span><span>Gaming</span></div>
              </a>
              <a className="proj tilt magnetic" data-cursor="view" href="#work" data-proj="4">
                <div className="proj__id">P/04</div>
                <h4>BingeTales</h4>
                <p>A first-of-its-kind reading app spanning Horror, Mythology, Mystery &amp; Classics.</p>
                <div className="proj__tags"><span>Android</span><span>Product</span></div>
              </a>
            </div>
          </div>
        </section>

        {/* ░░ STACK / PHYSICS PLAYGROUND ░░ */}
        <section className="stack" id="stack" data-screen-label="Stack">
          <div className="section-head reveal">
            <span className="section-no">03</span>
            <span className="section-kicker">{"// the stack — grab & throw it"}</span>
          </div>
          <h3 className="stack__hint reveal">Drag the chips. They're physical. <span>(go on)</span></h3>
          <div className="playground" id="playground">
            <canvas id="physicsCanvas" />
          </div>
        </section>

        {/* ░░ EDUCATION ░░ */}
        <section className="writing" id="education" data-screen-label="Education">
          <div className="section-head reveal">
            <span className="section-no">04</span>
            <span className="section-kicker">{"// education"}</span>
          </div>
          <div className="writing__grid">
            <div className="post reveal">
              <span className="post__src">2017</span>
              <h4>B.Tech, Computer Science</h4>
              <p>Amity University, Jaipur.</p>
              <span className="post__go">Bachelor's degree</span>
            </div>
            <div className="post reveal reveal-delay-1">
              <span className="post__src">2013</span>
              <h4>CBSE Class XII</h4>
              <p>Maheshwari Public School, Jawahar Nagar, Jaipur.</p>
              <span className="post__go">Senior secondary</span>
            </div>
            <div className="post reveal reveal-delay-2">
              <span className="post__src">2011</span>
              <h4>CBSE Class X</h4>
              <p>Maheshwari Public School, Jawahar Nagar, Jaipur.</p>
              <span className="post__go">Secondary</span>
            </div>
          </div>
        </section>

        {/* ░░ CONTACT ░░ */}
        <section className="contact" id="contact" data-screen-label="Contact">
          <div className="contact__inner">
            <p className="contact__kicker reveal">{"// got something to build?"}</p>
            <a className="contact__big magnetic" data-cursor="email" href="mailto:laksh.parnami@gmail.com">
              <span className="scramble-hover">LET'S&nbsp;TALK</span>
            </a>
            <div className="contact__links reveal">
              <a href="https://www.lakshay-parnami.com" target="_blank" rel="noopener noreferrer" className="magnetic" data-cursor="go">Website</a>
              <a href="mailto:laksh.parnami@gmail.com" className="magnetic" data-cursor="email">Email</a>
              <a href="tel:+918107958922" className="magnetic" data-cursor="go">Mobile</a>
            </div>
          </div>
          <footer className="footer">
            <span>© 2026 Lakshay Parnami</span>
            <span className="footer__mid">Built with too much enthusiasm</span>
            <button className="footer__top magnetic" data-cursor="up" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>↑ TOP</button>
          </footer>
        </section>
      </main>
    </div>
  );
};

export default Maximalist;
